import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lt } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, getApiUrl } from '../config/api';

interface AuditActor {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  department?: string;
}

interface ExhibitionAuditLogEntry {
  id: string;
  actionType: string;
  entityType: string;
  entityId: string | null;
  targetDate: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
  actor?: AuditActor | null;
}

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
}

const ACTION_TYPES = [
  'edukation_create',
  'edukation_update',
  'edukation_delete',
  'edukation_status_change',
  'edukation_participants_confirm',
  'edukation_participants_unconfirm',
  'excursion_create',
  'excursion_update',
  'excursion_delete',
  'excursion_status_change'
] as const;

function formatMonthKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

interface ExhibitionActivityLogProps {
  embedded?: boolean;
}

const ExhibitionActivityLog: React.FC<ExhibitionActivityLogProps> = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<ExhibitionAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [monthFilter, setMonthFilter] = useState<Date>(new Date());
  const [actorFilter, setActorFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ExhibitionAuditLogEntry | null>(null);

  const getActionLabel = (actionType: string) =>
    t(`exhibition.logs.actions.${actionType}`, actionType);

  const formatActor = (log: ExhibitionAuditLogEntry) =>
    log.actor ? `${log.actor.firstName} ${log.actor.lastName}` : '-';

  const renderSummary = (log: ExhibitionAuditLogEntry) => {
    const title =
      (log.afterState?.title as string | undefined) ||
      (log.beforeState?.title as string | undefined) ||
      '';
    if (log.actionType.includes('status_change')) {
      const from = (log.beforeState?.status as string) || (log.meta?.from as string) || '-';
      const to = (log.afterState?.status as string) || (log.meta?.to as string) || '-';
      return title ? `${title}: ${from} → ${to}` : `${from} → ${to}`;
    }
    if (log.actionType.includes('participants_confirm')) {
      const rawCount = log.afterState?.maxParticipants ?? log.beforeState?.maxParticipants;
      const count = typeof rawCount === 'number' ? rawCount : Number(rawCount);
      const countValue = Number.isFinite(count) ? count : undefined;
      return title
        ? t('exhibition.logs.summary.confirmed', '{{title}} ({{count}})', {
            title,
            count: countValue
          })
        : String(countValue ?? '-');
    }
    if (log.actionType.includes('participants_unconfirm')) {
      return title
        ? t('exhibition.logs.summary.unconfirmed', '{{title}} – atšaukta', { title })
        : t('exhibition.logs.summary.unconfirmedShort', 'Patvirtinimas atšauktas');
    }
    return title || log.entityId || '-';
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('month', formatMonthKey(monthFilter));
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (actorFilter) params.set('actorUserId', actorFilter);
      if (entityTypeFilter) params.set('entityType', entityTypeFilter);
      if (actionFilter) params.set('actionType', actionFilter);

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.EXHIBITION_LOGS)}?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch activity logs');
      }
      const json = await response.json();
      setLogs(json.data || []);
      setTotal(json.pagination?.total ?? 0);
    } catch (err) {
      console.error('Error fetching exhibition audit logs:', err);
      setError(t('exhibition.logs.fetchFailed', 'Nepavyko įkelti veiklos žurnalo'));
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [monthFilter, actorFilter, entityTypeFilter, actionFilter, page, limit, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.USERS), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        const list = (data.users || data || []) as UserOption[];
        if (!cancelled && Array.isArray(list)) {
          setUsers(
            list
              .filter((u) => u?.id && u.firstName)
              .sort((a, b) =>
                `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'lt')
              )
          );
        }
      } catch {
        // Actor filter stays empty if users list is unavailable
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [monthFilter, actorFilter, entityTypeFilter, actionFilter, limit]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 2,
            mb: 2
          }}
        >
          <DatePicker
            label={t('exhibition.logs.filters.month', 'Veiksmų mėnuo')}
            views={['year', 'month']}
            value={monthFilter}
            onChange={(value: Date | null) => {
              if (value) setMonthFilter(value);
            }}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <FormControl fullWidth>
            <InputLabel>{t('exhibition.logs.filters.actor', 'Kas pakeitė')}</InputLabel>
            <Select
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              label={t('exhibition.logs.filters.actor', 'Kas pakeitė')}
            >
              <MenuItem value="">{t('common.all', 'Visi')}</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {`${u.firstName} ${u.lastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t('exhibition.logs.filters.entityType', 'Tipas')}</InputLabel>
            <Select
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
              label={t('exhibition.logs.filters.entityType', 'Tipas')}
            >
              <MenuItem value="">{t('common.all', 'Visi')}</MenuItem>
              <MenuItem value="edukation">
                {t('exhibition.logs.entity.edukation', 'Edukacija')}
              </MenuItem>
              <MenuItem value="excursion">
                {t('exhibition.logs.entity.excursion', 'Ekskursija')}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t('exhibition.logs.filters.action', 'Veiksmas')}</InputLabel>
            <Select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              label={t('exhibition.logs.filters.action', 'Veiksmas')}
            >
              <MenuItem value="">{t('common.all', 'Visi')}</MenuItem>
              {ACTION_TYPES.map((action) => (
                <MenuItem key={action} value={action}>
                  {getActionLabel(action)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('exhibition.logs.columns.time', 'Laikas')}</TableCell>
                <TableCell>{t('exhibition.logs.columns.action', 'Veiksmas')}</TableCell>
                <TableCell>{t('exhibition.logs.columns.entity', 'Įrašas')}</TableCell>
                <TableCell>{t('exhibition.logs.columns.date', 'Data')}</TableCell>
                <TableCell>{t('exhibition.logs.columns.actor', 'Pakeitė')}</TableCell>
                <TableCell>{t('exhibition.logs.columns.summary', 'Santrauka')}</TableCell>
                <TableCell>{t('common.actions', 'Veiksmai')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    {t('exhibition.logs.noData', 'Įrašų nerasta')}
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{new Date(log.createdAt).toLocaleString('lt-LT')}</TableCell>
                    <TableCell>{getActionLabel(log.actionType)}</TableCell>
                    <TableCell>
                      {t(`exhibition.logs.entity.${log.entityType}`, log.entityType)}
                    </TableCell>
                    <TableCell>{log.targetDate || '-'}</TableCell>
                    <TableCell>{formatActor(log)}</TableCell>
                    <TableCell>{renderSummary(log)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedLog(log);
                          setDetailsOpen(true);
                        }}
                      >
                        {t('exhibition.logs.viewDetails', 'Detalės')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="body2">
            {t('exhibition.logs.pagination.total', 'Iš viso: {{count}}', { count: total })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              disabled={page <= 1 || loading}
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            >
              {t('common.previous', 'Ankstesnis')}
            </Button>
            <Typography variant="body2">{page}</Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={loading || page * limit >= total}
              onClick={() => setPage((prev) => prev + 1)}
            >
              {t('common.next', 'Kitas')}
            </Button>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}>
                {[25, 50, 100].map((value) => (
                  <MenuItem key={value} value={String(value)}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{t('exhibition.logs.detailsTitle', 'Veiklos detalės')}</DialogTitle>
          <DialogContent>
            {selectedLog && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
                <Typography variant="body2">
                  <strong>{t('exhibition.logs.columns.action', 'Veiksmas')}:</strong>{' '}
                  {getActionLabel(selectedLog.actionType)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('exhibition.logs.columns.actor', 'Pakeitė')}:</strong>{' '}
                  {formatActor(selectedLog)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('exhibition.logs.columns.date', 'Data')}:</strong>{' '}
                  {selectedLog.targetDate || '-'}
                </Typography>
                <TextField
                  label={t('exhibition.logs.beforeState', 'Būsena prieš')}
                  value={JSON.stringify(selectedLog.beforeState || {}, null, 2)}
                  multiline
                  minRows={6}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label={t('exhibition.logs.afterState', 'Būsena po')}
                  value={JSON.stringify(selectedLog.afterState || {}, null, 2)}
                  multiline
                  minRows={6}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label={t('exhibition.logs.meta', 'Meta')}
                  value={JSON.stringify(selectedLog.meta || {}, null, 2)}
                  multiline
                  minRows={3}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ExhibitionActivityLog;
